import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth, ROLE_SI_ADMIN } from '@/contexts/AuthContext';
import { guardioesService } from './guardioesService';
import type { Guardiao } from './guardioesService';

export const GUARDIOES_QUERY_KEY = ['si', 'guardioes'] as const;

/** Em teste: só estas roles veem o selo (pra não "vazar" antes de liberar a pílula). Pra liberar
 * pra todos, deixar vazio — e tirar `liberadoSoPara` da aba Guardiões em siPortal.definitions. */
export const GUARDIOES_LIBERADO_SO_PARA: readonly string[] = [ROLE_SI_ADMIN];

export function useGuardioesLiberado(): boolean {
  const { userHasRole } = useAuth();
  return GUARDIOES_LIBERADO_SO_PARA.length === 0 || GUARDIOES_LIBERADO_SO_PARA.some((r) => userHasRole(r));
}

/** Lista de Guardiões (pequena; cacheada por 10 min) + busca por e-mail/colaborador pro selo. */
export function useGuardioes() {
  const liberado = useGuardioesLiberado();
  const consulta = useQuery({
    queryKey: GUARDIOES_QUERY_KEY,
    queryFn: () => guardioesService.listar(),
    enabled: liberado,
    staleTime: 10 * 60 * 1000,
  });
  const indice = useMemo(() => {
    const porEmail = new Map<string, Guardiao>();
    const porColaborador = new Map<string, Guardiao>();
    for (const g of consulta.data ?? []) {
      porEmail.set(g.email.toLowerCase(), g);
      if (g.colaboradorId) porColaborador.set(g.colaboradorId, g);
    }
    return { porEmail, porColaborador };
  }, [consulta.data]);

  const guardiaoDe = (pessoa: { email?: string | null; colaboradorId?: string | null }): Guardiao | null =>
    (pessoa.colaboradorId ? indice.porColaborador.get(pessoa.colaboradorId) : undefined) ??
    (pessoa.email ? indice.porEmail.get(pessoa.email.toLowerCase()) : undefined) ??
    null;

  return { ...consulta, liberado, guardiaoDe };
}
